import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchProductJson, imageAtWidth } from '@theme/utilities';

/** One scroll per page load, so a picker that connects later keeps the page where it is. */
let anchorScrollDone = false;

/**
 * @typedef {object} BundleVariant
 * @property {number} id
 * @property {string} title
 * @property {boolean} available
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {string} [priceFormatted]
 * @property {string|null} [compareAtFormatted]
 * @property {string} [unitPriceFormatted]
 * @property {string|null} [savingsBadgeFormatted]
 * @property {string[]} options
 */

/**
 * @typedef {object} BundleProduct
 * @property {number} id
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {number} packQuantity
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {string} featuredImage
 * @property {boolean} available
 * @property {BundleVariant[]} variants
 * @property {string[]} options
 * @property {string} [badgeHtml]
 * @property {Record<string, string|null>} [colorSwatches] - Swatch background per color name
 */

/**
 * @typedef {object} PromoBundleProduct
 * @property {number} id
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {number} paidQuantity
 * @property {number} freeQuantity
 * @property {number} totalItems
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {string} featuredImage
 * @property {boolean} available
 * @property {BundleVariant[]} variants
 * @property {string[]} options
 * @property {string} badge
 * @property {string} [badgeHtml]
 */

/**
 * @typedef {object} RegularPack
 * @property {number} id
 * @property {string} handle
 * @property {number} packQuantity
 * @property {number} price
 * @property {boolean} available
 * @property {{id: number, available: boolean, options: string[]}[]} variants
 * @property {string[]} options
 */

/**
 * @typedef {object} BundleData
 * @property {number} currentProductId
 * @property {number} currentVariantId
 * @property {string[]} currentOptions
 * @property {string[]} productOptions
 * @property {boolean} isPromoMode
 * @property {(BundleProduct|PromoBundleProduct)[]} bundles
 * @property {RegularPack[]} [regularPacks]
 */

/** Width the card packshots are requested at, the same for every card. */
const CARD_IMAGE_WIDTH = 200;

export default class BundlePicker extends Component {
  /** @type {BundleData | null} */
  #bundleData = null;
  /** @type {number | null} */
  #selectedBundleId = null;
  /** @type {string[]} */
  #currentOptions = [];
  /** @type {string | null} BOX-380: selected color that only the selected pack has, see #syncColorOptions */
  #packOnlyColor = null;
  /** @type {AbortController} */
  #abortController = new AbortController();
  /** @type {string} */
  #originalTitleText = '';
  /** @type {string} */
  #savedPriceHTML = '';
  /** @type {boolean} */
  #isPromoMode = false;
  /** @type {string | null} */
  #freePackColor = null;
  /** @type {number | null} */
  #freePackVariantId = null;
  /** @type {RegularPack[]} */
  #regularPacks = [];
  /** @type {boolean} */
  #isSubmitting = false;
  /** @type {boolean} */
  #addToCartIntercepted = false;
  /** @type {{src: string, srcset: string}|null} Saved gallery image for restoration */
  #originalImageAttrs = null;
  /** @type {{imageSrc: string, available: string}|null} Saved sticky bar state */
  #originalStickyState = null;

  connectedCallback() {
    super.connectedCallback();

    this.#loadBundleData();
    this.#saveOriginals();
    this.#setupEventListeners();
    this.#updateAllCardPrices();
    this.#scrollToAnchor();

    if (this.#isPromoMode) {
      this.#buildFreePackSwatches();
      this.#preSelectFreePackColor();
      this.#interceptAddToCart();
      this.#interceptFreePackClicks();
      this.#updateAddToCartState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Brings the picker into view when the page is opened on its own anchor, so a mailing can
   * link straight to the bundles. The browser jumps there itself, but it does so while the
   * gallery above is still settling, which leaves the picker off screen — hence taking the
   * position again once the page has loaded. A copy inside the quick add dialog is skipped:
   * it is another product's picker and the page should stay where the customer left it.
   */
  #scrollToAnchor() {
    if (anchorScrollDone || !this.id || `#${this.id}` !== window.location.hash) return;
    if (this.closest('dialog')) return;

    anchorScrollDone = true;

    const scroll = () => requestAnimationFrame(() => this.scrollIntoView({ block: 'start' }));

    if (document.readyState === 'complete') {
      scroll();
    } else {
      window.addEventListener('load', scroll, { once: true });
    }
  }

  /** @returns {boolean} */
  #loadBundleData() {
    if (this.#bundleData) return true;

    const dataScript = this.querySelector('script[data-bundle-data]');
    if (!dataScript?.textContent) return false;

    try {
      this.#bundleData = JSON.parse(dataScript.textContent);
      this.#selectedBundleId = this.#bundleData?.currentProductId ?? null;
      this.#currentOptions = this.#bundleData?.currentOptions ?? [];
      this.#isPromoMode = this.#bundleData?.isPromoMode ?? false;
      this.#regularPacks = this.#bundleData?.regularPacks ?? [];

      if (this.#bundleData?.bundles) {
        if (this.#isPromoMode) {
          this.#bundleData.bundles.sort((a, b) => {
            const aTotal = /** @type {PromoBundleProduct} */ (a).totalItems ?? 0;
            const bTotal = /** @type {PromoBundleProduct} */ (b).totalItems ?? 0;
            return aTotal - bTotal;
          });
        } else {
          this.#bundleData.bundles.sort((a, b) => {
            const aPack = /** @type {BundleProduct} */ (a).packQuantity ?? 0;
            const bPack = /** @type {BundleProduct} */ (b).packQuantity ?? 0;
            return aPack - bPack;
          });
        }
      }
      return true;
    } catch (error) {
      console.error('BundlePicker: Failed to parse bundle data', error);
      return false;
    }
  }

  #setupEventListeners() {
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this), { signal });
    target?.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.closest('fieldset') === this.#colorFieldset) {
          this.#packOnlyColor = null;
        }
      },
      { signal }
    );
  }

  #saveOriginals() {
    this.#originalTitleText = this.#titleElements[0]?.textContent ?? '';
    this.#savedPriceHTML = this.#priceContainers[0]?.innerHTML ?? '';
  }

  /** @returns {Element[]} */
  get #titleElements() {
    const section = this.closest('.shopify-section');
    return [
      section?.querySelector('.product-details h1'),
      section?.querySelector('[data-testid="sticky-product-title"]'),
    ].filter((element) => element instanceof Element);
  }

  /** @returns {Element[]} */
  get #priceContainers() {
    const section = this.closest('.shopify-section');
    return [
      section?.querySelector('product-price [ref="priceContainer"]'),
      section?.querySelector('.sticky-add-to-cart__price [ref="priceContainer"]'),
    ].filter((element) => element instanceof Element);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',');
  }

  /** @param {string} title */
  #updatePageTitle(title) {
    for (const element of this.#titleElements) element.textContent = title;
  }

  /**
   * @param {number} price
   * @param {number} compareAtPrice
   */
  #updatePagePrice(price, compareAtPrice) {
    const containers = this.#priceContainers;
    if (containers.length === 0) return;

    const formattedPrice = this.#formatMoney(price);
    const hasCompare = compareAtPrice > price;

    let html = `<span class="price">${formattedPrice}</span>`;
    if (hasCompare) {
      html += `<span class="compare-at-price">${this.#formatMoney(compareAtPrice)}</span>`;
    }

    // The price promises one thing at a time. The label comes from the selected card,
    // because what a pack hands over is a different number per pack.
    const giftLabel = this.#selectedGiftLabel();
    if (giftLabel) {
      html += giftLabel;
    } else if (hasCompare) {
      const discountPercentage = Math.round((compareAtPrice - price) / compareAtPrice * 100);
      const template = this.dataset.discountText || '__PCT__% off';
      html += `<span class="discount-percentage discount-percentage--label">${template.replace('__PCT__', discountPercentage)}</span>`;
    }

    for (const container of containers) container.innerHTML = html;
  }

  /**
   * The gift label of the card the customer has selected, as markup, or null when that
   * pack has no campaign.
   *
   * @returns {string | null}
   */
  #selectedGiftLabel() {
    const card = this
      .querySelector(`.bundle-card__input[value="${this.#selectedBundleId}"]`)
      ?.closest('.bundle-card');

    const label = card?.querySelector('.bundle-badge--gift');
    return label instanceof HTMLElement && !label.hidden ? label.outerHTML : null;
  }

  #applyBundleDisplay() {
    if (!this.#selectedBundleId || !this.#bundleData) return;

    const selectedBundle = this.#bundleData.bundles.find((b) => b.id === this.#selectedBundleId);
    if (!selectedBundle) return;

    const matchingVariant = this.#findMatchingVariant(selectedBundle, this.#currentOptions);

    this.#updatePageTitle(selectedBundle.title);
    this.#updatePagePrice(
      matchingVariant?.price ?? selectedBundle.price,
      matchingVariant?.compareAtPrice ?? selectedBundle.compareAtPrice
    );
  }

  #restoreOriginalDisplay() {
    for (const element of this.#titleElements) element.textContent = this.#originalTitleText;

    if (!this.#savedPriceHTML) return;
    for (const container of this.#priceContainers) container.innerHTML = this.#savedPriceHTML;
  }

  /**
   * Reads the --swatch-background value from the main color picker's swatch
   * matching the given color name. Returns null if not found.
   * @param {string} colorName
   * @returns {string | null}
   */
  #getMainSwatchBackground(colorName) {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const colorLower = colorName.toLowerCase();
    const mainSwatches = section.querySelectorAll('variant-picker-component .swatch, fieldset .swatch');

    for (const el of mainSwatches) {
      const radio = el.closest('label')?.querySelector('input[type="radio"]');
      if (radio instanceof HTMLInputElement && radio.value.toLowerCase() === colorLower) {
        return el.style.getPropertyValue('--swatch-background') || null;
      }
    }

    return null;
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;

    const previousColor = this.#getCurrentColor();

    this.#loadBundleData();

    if (variant.options && variant.options.length > 0) {
      this.#currentOptions = variant.options;
    }

    // The variant picker was just morphed back to the host product's full color list
    this.#syncColorOptions();

    // Refresh every bundle card's price to match the newly selected color/size
    this.#updateAllCardPrices();

    const [pagePrice] = this.#priceContainers;
    if (pagePrice) this.#savedPriceHTML = pagePrice.innerHTML;

    // Reset saved gallery and sticky bar state — both re-render to the host product on variant update
    this.#originalImageAttrs = null;
    this.#originalStickyState = null;

    if (this.#selectedBundleId && this.#selectedBundleId !== this.#bundleData?.currentProductId) {
      this.#updateFormVariant();
      this.#applyBundleDisplay();
      // Delay: media-gallery and sticky-add-to-cart re-render synchronously in their own variantUpdate
      // handlers. setTimeout ensures we swap AFTER the new markup is in the DOM.
      setTimeout(() => {
        this.#updateBundleImages();
        this.#updateGalleryBadges();
        this.#updateStickyBar();
      }, 0);
    }

    const colorChanged = previousColor?.toLowerCase() !== this.#getCurrentColor()?.toLowerCase();

    if (this.#isPromoMode) {
      this.#buildFreePackSwatches();
      this.#interceptFreePackClicks();
      if (colorChanged) {
        this.#preSelectFreePackColor();
      } else {
        this.#resolveFreePackVariant();
      }
      this.#updateAddToCartState();
    }

    if (colorChanged) {
      this.#updateAllCardImages();
    }
  }

  /** @param {Event} event */
  handleBundleChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const selectedProductId = parseInt(event.target.value, 10);
    if (isNaN(selectedProductId)) return;

    this.#loadBundleData();

    const previousBundleId = this.#selectedBundleId;
    this.#updateSelectedState(selectedProductId);
    this.#selectedBundleId = selectedProductId;

    if (this.#isPromoMode && previousBundleId !== selectedProductId) {
      this.#freePackColor = null;
      this.#freePackVariantId = null;
      this.#buildFreePackSwatches();
      this.#preSelectFreePackColor();
      this.#updateAddToCartState();
    }

    // First, so the form, gallery and sticky bar below already use a color this pack offers
    this.#syncColorOptions();

    if (selectedProductId === this.#bundleData?.currentProductId) {
      this.#resetFormToCurrentProduct();
      this.#restoreOriginalDisplay();
      this.#restoreGalleryImage();
      this.#updateGalleryBadges();
      this.#updateStickyBar();
      return;
    }

    this.#updateFormVariant();
    this.#applyBundleDisplay();
    this.#updateBundleImages();
    this.#updateGalleryBadges();
    this.#updateStickyBar();
  }

  /** @param {number} selectedProductId */
  #updateSelectedState(selectedProductId) {
    const cards = this.querySelectorAll('.bundle-card');
    cards.forEach((card) => {
      const productId = parseInt(card.getAttribute('data-product-id') ?? '', 10);
      card.classList.toggle('bundle-card--selected', productId === selectedProductId);
    });

    const selectedBundle = this.#bundleData?.bundles.find((b) => b.id === selectedProductId);
    if (selectedBundle) {
      const currentSpan = this.querySelector('.bundle-picker__current');
      if (currentSpan) {
        if (this.#isPromoMode) {
          const promo = /** @type {PromoBundleProduct} */ (selectedBundle);
          const itemLabel = currentSpan.textContent?.replace(/^[\d+]+\s*/, '') ?? '';
          currentSpan.textContent = `${promo.paidQuantity}+${promo.freeQuantity} ${itemLabel}`;
        } else {
          const pack = /** @type {BundleProduct} */ (selectedBundle);
          // BOX-318: rebuilt from its parts, because the free part behind the name belongs
          // to the selected pack. Stripping the number off the old text would carry the
          // previous pack's suffix along.
          const base =
            currentSpan instanceof HTMLElement && currentSpan.dataset.baseLabel
              ? currentSpan.dataset.baseLabel
              : (currentSpan.textContent?.replace(/^\d+\s*/, '') ?? 'boxers');
          const card = this.querySelector(`.bundle-card[data-product-id="${selectedProductId}"]`);
          const suffix =
            card instanceof HTMLElement ? (card.dataset.giftSuffix ?? '').trim() : '';

          currentSpan.textContent = `${pack.packQuantity} ${base}${suffix ? ' ' + suffix : ''}`;
        }
      }
    }
  }

  #resetFormToCurrentProduct() {
    const productForm = this.closest('.shopify-section')?.querySelector('product-form-component');
    const variantInput = productForm?.querySelector('input[name="id"]');

    if (variantInput instanceof HTMLInputElement && this.#bundleData?.currentVariantId) {
      const currentBundle = this.#bundleData.bundles.find((b) => b.id === this.#bundleData?.currentProductId);
      if (currentBundle) {
        const matchingVariant = this.#findMatchingVariant(currentBundle, this.#currentOptions);
        if (matchingVariant) {
          variantInput.value = matchingVariant.id.toString();
          // A sold-out pack may have disabled the button; the host variant decides again
          if (!this.#isPromoMode) this.#updateAddToCartState(matchingVariant.available);
        }
      }
    }
  }

  #updateFormVariant() {
    if (!this.#selectedBundleId || !this.#bundleData) return;

    const selectedBundle = this.#bundleData.bundles.find((b) => b.id === this.#selectedBundleId);
    if (!selectedBundle) return;

    const matchingVariant = this.#findMatchingVariant(selectedBundle, this.#currentOptions);
    if (!matchingVariant) return;

    // In promo mode we intercept add-to-cart, no need to update the form variant
    if (!this.#isPromoMode) {
      const productForm = this.closest('.shopify-section')?.querySelector('product-form-component');
      const variantInput = productForm?.querySelector('input[name="id"]');

      if (variantInput instanceof HTMLInputElement) {
        variantInput.value = matchingVariant.id.toString();
      }

      this.#updateAddToCartState(matchingVariant.available);
    }
  }

  /**
   * @param {BundleProduct|PromoBundleProduct} bundle
   * @param {string[]} options
   * @returns {BundleVariant | null}
   */
  #findMatchingVariant(bundle, options) {
    if (!bundle.variants || bundle.variants.length === 0) return null;
    if (!options || options.length === 0) {
      return bundle.variants.find((v) => v.available) ?? bundle.variants[0];
    }

    for (const variant of bundle.variants) {
      if (this.#optionsMatch(variant.options, options)) {
        return variant;
      }
    }

    let bestMatch = null;
    let bestMatchCount = 0;
    for (const variant of bundle.variants) {
      const matchCount = this.#countMatchingOptions(variant.options, options);
      if (matchCount > bestMatchCount) {
        bestMatchCount = matchCount;
        bestMatch = variant;
      }
    }

    return bestMatch;
  }

  #optionsMatch(variantOptions, selectedOptions) {
    if (variantOptions.length !== selectedOptions.length) return false;
    for (let i = 0; i < variantOptions.length; i++) {
      if (variantOptions[i].toLowerCase() !== selectedOptions[i].toLowerCase()) return false;
    }
    return true;
  }

  #countMatchingOptions(variantOptions, selectedOptions) {
    let count = 0;
    const minLength = Math.min(variantOptions.length, selectedOptions.length);
    for (let i = 0; i < minLength; i++) {
      if (variantOptions[i].toLowerCase() === selectedOptions[i].toLowerCase()) count++;
    }
    return count;
  }

  // ─── Color Options per Pack ───────────────────────────────────────

  /** @returns {HTMLElement | null} */
  get #colorFieldset() {
    const fieldset = this.closest('.shopify-section, dialog')?.querySelector(
      'variant-picker fieldset[data-fieldset-index="0"]'
    );
    return fieldset instanceof HTMLElement ? fieldset : null;
  }

  /**
   * BOX-380: the color picker is rendered for the host product, but packs differ in colors
   * (e.g. Multicolor only exists on the 2- and 4-pack polos). Hides the colors the selected
   * pack lacks, draws the ones only the pack has, and keeps the selection on a color the
   * pack offers, so the picked color is the one that lands in the cart.
   */
  #syncColorOptions() {
    const fieldset = this.#colorFieldset;
    if (!fieldset || !this.#bundleData) return;

    const bundle = this.#bundleData.bundles.find((b) => b.id === this.#selectedBundleId);
    const isHost = this.#selectedBundleId === this.#bundleData.currentProductId;
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]')).filter(
      (radio) => radio instanceof HTMLInputElement
    );
    const bundleColors = [...new Set(bundle?.variants.map((v) => v.options[0]).filter(Boolean) ?? [])];
    const inBundle = (/** @type {string} */ color) =>
      bundleColors.some((c) => c.toLowerCase() === color.toLowerCase());

    const offered = radios.filter((radio) => isHost || !bundle || inBundle(radio.value));
    // Promo cards resolve their free pack from the host colors, so they never get extra ones
    const packOnly =
      isHost || this.#isPromoMode
        ? []
        : bundleColors.filter((color) => !radios.some((r) => r.value.toLowerCase() === color.toLowerCase()));
    // A pack that shares no color with the host keeps the full list rather than an empty one
    const visible = offered.length > 0 || packOnly.length > 0 ? offered : radios;

    for (const radio of radios) {
      const label = radio.closest('label');
      if (label) label.hidden = !visible.includes(radio);
    }
    this.#renderPackOnlySwatches(fieldset, bundle, packOnly, bundleColors);
    this.#reflowColorList(fieldset);
    this.#keepSelectionOffered(fieldset, bundle, visible, packOnly);
  }

  /**
   * Draws a swatch for every color only the selected pack has. These are buttons, not radios:
   * the variant picker reads every checked radio as an option value of the host product, and
   * the host has no option value for these colors.
   * @param {HTMLElement} fieldset
   * @param {BundleProduct|PromoBundleProduct|undefined} bundle
   * @param {string[]} colors
   * @param {string[]} bundleColors - All colors of the pack, in the pack's own order
   */
  #renderPackOnlySwatches(fieldset, bundle, colors, bundleColors) {
    for (const label of fieldset.querySelectorAll('[data-pack-only-color]')) label.remove();

    const list = fieldset.querySelector('overflow-list') ?? fieldset;
    const hostLabels = Array.from(list.querySelectorAll(':scope > label:not([hidden])'));
    const position = (/** @type {string} */ color) =>
      bundleColors.findIndex((c) => c.toLowerCase() === color.toLowerCase());

    for (const color of colors) {
      const label = document.createElement('label');
      label.className = 'variant-option__button-label variant-option__button-label--has-swatch';
      label.dataset.packOnlyColor = color;

      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', color);
      button.setAttribute('aria-pressed', String(this.#isPackOnlyColor(color)));
      button.addEventListener('click', () => this.#selectPackOnlyColor(color));

      const swatch = document.createElement('span');
      swatch.className = 'swatch swatch--unscaled';
      const background = /** @type {BundleProduct} */ (bundle)?.colorSwatches?.[color];
      if (background) {
        swatch.style.setProperty('--swatch-background', background);
      } else {
        swatch.classList.add('swatch--unavailable');
      }

      button.append(swatch);
      label.append(button);

      // Same spot as on the pack's own page: before the first host color that comes later
      const next = hostLabels.find((host) => {
        const value = host.querySelector('input')?.value ?? '';
        return position(value) > position(color);
      });
      list.insertBefore(label, next ?? list.querySelector(':scope > [slot="more"]'));
    }
  }

  /**
   * @param {HTMLElement} fieldset
   * @param {BundleProduct|PromoBundleProduct|undefined} bundle
   * @param {HTMLInputElement[]} visible
   * @param {string[]} packOnly
   */
  #keepSelectionOffered(fieldset, bundle, visible, packOnly) {
    const kept = packOnly.find((color) => this.#isPackOnlyColor(color));
    if (kept) {
      // The pack's own spelling, so its variants match exactly
      this.#packOnlyColor = kept;
      this.#markPackOnlyColor(fieldset);
      this.#applyPackOnlyColor();
      return;
    }
    const droppedColor = this.#packOnlyColor;
    this.#packOnlyColor = null;

    if (!bundle || visible.some((radio) => radio.checked)) return;

    // Prefer a color the pack has in stock in the current size, and a host color over a drawn one
    const available = (/** @type {string} */ color) => this.#hasAvailableVariant(bundle, color);
    const radio = visible.find((r) => available(r.value)) ?? (packOnly.some(available) ? null : visible[0]);
    const color = radio ? null : (packOnly.find(available) ?? packOnly[0]);
    if (!radio && !color) return;

    console.info('BundlePicker: color not offered by selected pack, switching', {
      bundleId: bundle.id,
      from: droppedColor ?? this.#getCurrentColor(),
      to: radio?.value ?? color,
    });

    if (radio) {
      // The host fetch confirms this later; until then the caller already resolves the new color
      this.#currentOptions = [radio.value, ...this.#currentOptions.slice(1)];
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (color) {
      this.#packOnlyColor = color;
      this.#markPackOnlyColor(fieldset);
      this.#applyPackOnlyColor();
    }
  }

  /** @param {string} color */
  #selectPackOnlyColor(color) {
    const fieldset = this.#colorFieldset;
    if (!fieldset) return;

    console.info('BundlePicker: pack-only color selected', { bundleId: this.#selectedBundleId, color });
    this.#packOnlyColor = color;
    this.#markPackOnlyColor(fieldset);
    this.#applyPackOnlyColor();

    this.#updateFormVariant();
    this.#applyBundleDisplay();
    this.#updateBundleImages();
    this.#updateStickyBar();
    this.#updateAllCardPrices();
    this.#updateAllCardImages();
  }

  /**
   * Shows the pack-only color as the selected one. The host radios are unchecked, so a size
   * change asks the host for that size alone and the next variant update keeps this color.
   * @param {HTMLElement} fieldset
   */
  #markPackOnlyColor(fieldset) {
    for (const radio of fieldset.querySelectorAll('input[type="radio"]')) {
      if (radio instanceof HTMLInputElement) radio.checked = false;
    }
    for (const button of fieldset.querySelectorAll('[data-pack-only-color] > button')) {
      const label = button.parentElement;
      button.setAttribute('aria-pressed', String(this.#isPackOnlyColor(label?.dataset.packOnlyColor ?? '')));
    }
    const legend = fieldset.querySelector('.variant-option__swatch-value');
    if (legend && this.#packOnlyColor) legend.textContent = this.#packOnlyColor;
  }

  /** @param {string} color */
  #isPackOnlyColor(color) {
    return !!this.#packOnlyColor && color.toLowerCase() === this.#packOnlyColor.toLowerCase();
  }

  #applyPackOnlyColor() {
    if (!this.#packOnlyColor) return;
    this.#currentOptions = [this.#packOnlyColor, ...this.#currentOptions.slice(1)];
  }

  /**
   * @param {BundleProduct|PromoBundleProduct} bundle
   * @param {string} color
   */
  #hasAvailableVariant(bundle, color) {
    const size = this.#getCurrentSize()?.toLowerCase();
    return bundle.variants.some(
      (v) =>
        v.available &&
        v.options[0]?.toLowerCase() === color.toLowerCase() &&
        (!size || v.options[1]?.toLowerCase() === size)
    );
  }

  /**
   * The overflow-list only measures its items on resize or child changes, so hiding labels
   * leaves the "+" toggle counting the old set. Its `reflow` listener exists once it has
   * initialised, and initialising measures anyway, so an early event is safely ignored.
   * An expanded list (`disabled="true"`) shows everything and must not collapse again.
   * @param {HTMLElement} fieldset
   */
  #reflowColorList(fieldset) {
    const list = fieldset.querySelector('overflow-list');
    if (!list || list.getAttribute('disabled') === 'true') return;
    list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
  }

  // ─── Gallery Image Update ─────────────────────────────────────────

  /**
   * Updates the PDP gallery and sticky bar image to match the selected bundle and color.
   * Fetches the bundle product's JSON to find the per-variant image,
   * then swaps it into the media gallery (slideshow + grid) and the sticky bar.
   */
  async #updateBundleImages() {
    if (!this.#selectedBundleId || !this.#bundleData) return;
    if (this.#selectedBundleId === this.#bundleData.currentProductId) return;

    const selectedBundle = this.#bundleData.bundles.find((b) => b.id === this.#selectedBundleId);
    if (!selectedBundle?.handle) return;

    const matchingVariant = this.#findMatchingVariant(selectedBundle, this.#currentOptions);
    if (!matchingVariant) return;

    const product = await fetchProductJson(selectedBundle.handle);
    // A slow fetch must not overwrite a bundle the shopper has switched to meanwhile
    if (!product || this.#selectedBundleId !== selectedBundle.id) return;

    // Find the variant's associated image via image_id
    const variant = product.variants?.find((v) => v.id === matchingVariant.id);
    const imageId = variant?.image_id;

    let imageUrl = null;
    if (imageId) {
      const image = product.images?.find((img) => img.id === imageId);
      if (image?.src) imageUrl = image.src;
    }

    // Fallback: product featured image
    if (!imageUrl && product.image?.src) {
      imageUrl = product.image.src;
    }

    if (imageUrl) {
      const separator = imageUrl.includes('?') ? '&' : '?';
      this.#swapGalleryImage(`${imageUrl}${separator}width=1200`);
      this.#swapStickyImage(`${imageUrl}${separator}width=120`);
    }
  }

  /**
   * Saves the current gallery image attributes (once) for later restoration.
   */
  #saveOriginalGalleryImage() {
    if (this.#originalImageAttrs) return;

    const mediaGallery = this.#getMediaGallery();
    if (!mediaGallery) return;

    const img =
      mediaGallery.querySelector('slideshow-slide .product-media__image') ||
      mediaGallery.querySelector('.media-gallery__grid .product-media__image');
    if (!img) return;

    this.#originalImageAttrs = {
      src: img.src,
      srcset: img.getAttribute('srcset') ?? '',
    };
  }

  /**
   * Swaps the gallery image to the given URL.
   * Targets both slideshow (mobile) and grid (desktop) views.
   * @param {string} imageUrl
   */
  #swapGalleryImage(imageUrl) {
    const mediaGallery = this.#getMediaGallery();
    if (!mediaGallery) return;

    this.#saveOriginalGalleryImage();

    // Navigate slideshow to first slide (mobile view)
    /** @type {any} */ (mediaGallery)?.slideshow?.select?.(0, undefined, { animate: false });

    const imgs = [
      mediaGallery.querySelector('slideshow-slide .product-media__image'),
      mediaGallery.querySelector('.media-gallery__grid .product-media__image'),
    ].filter(Boolean);

    for (const img of imgs) {
      img.removeAttribute('srcset');
      img.src = imageUrl;
    }
  }

  /**
   * Restores the gallery image to the original state (before bundle switch).
   */
  #restoreGalleryImage() {
    if (!this.#originalImageAttrs) return;

    const mediaGallery = this.#getMediaGallery();
    if (!mediaGallery) return;

    const imgs = [
      mediaGallery.querySelector('slideshow-slide .product-media__image'),
      mediaGallery.querySelector('.media-gallery__grid .product-media__image'),
    ].filter(Boolean);

    for (const img of imgs) {
      img.src = this.#originalImageAttrs.src;
      if (this.#originalImageAttrs.srcset) {
        img.setAttribute('srcset', this.#originalImageAttrs.srcset);
      }
    }

    this.#originalImageAttrs = null;
  }

  /**
   * The media gallery for this product section.
   * @returns {Element|null}
   */
  #getMediaGallery() {
    return this.closest('.shopify-section')?.querySelector('media-gallery') ?? null;
  }

  /**
   * Swaps the gallery badges to the ones belonging to the selected bundle, including
   * back to the product the page was opened on — every pack carries its own markup.
   * It is pre-rendered in Liquid because the badges come from metafields, which
   * `/products/<handle>.js` does not expose.
   */
  #updateGalleryBadges() {
    const selectedBundle = this.#bundleData?.bundles.find((b) => b.id === this.#selectedBundleId);
    if (!selectedBundle) return;

    const slots = this.#getMediaGallery()?.querySelectorAll('[data-pdp-badges-slot]') ?? [];
    for (const slot of slots) {
      slot.innerHTML = selectedBundle.badgeHtml ?? '';
    }
  }

  // ─── Sticky Add-to-Cart Bar ───────────────────────────────────────

  /** @returns {HTMLElement|null} */
  #getStickyBar() {
    const sticky = this.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
    return sticky instanceof HTMLElement ? sticky : null;
  }

  /**
   * Mirrors the selected bundle's availability into the sticky add-to-cart bar. The bar only
   * knows the product the page was opened on and re-renders to it on every variant update.
   * Title and price follow via #updatePageTitle/#updatePagePrice, the image once
   * #updateBundleImages has fetched it.
   */
  #updateStickyBar() {
    const sticky = this.#getStickyBar();
    if (!sticky || !this.#bundleData) return;

    if (this.#selectedBundleId === this.#bundleData.currentProductId) {
      this.#restoreStickyBar(sticky);
    } else {
      this.#applyBundleToStickyBar(sticky);
    }
    this.#syncStickyButton();
  }

  /** @param {HTMLElement} sticky */
  #applyBundleToStickyBar(sticky) {
    const selectedBundle = this.#bundleData?.bundles.find((b) => b.id === this.#selectedBundleId);
    if (!selectedBundle) return;

    const variant = this.#findMatchingVariant(selectedBundle, this.#currentOptions);
    if (!variant) return;

    this.#saveOriginalStickyBar(sticky);

    // The bar hides itself while this is not 'true'
    sticky.dataset.variantAvailable = String(variant.available);
  }

  /** @param {HTMLElement} sticky */
  #saveOriginalStickyBar(sticky) {
    if (this.#originalStickyState) return;

    this.#originalStickyState = {
      imageSrc: sticky.querySelector('[ref="productImage"]')?.getAttribute('src') ?? '',
      available: sticky.dataset.variantAvailable ?? '',
    };
  }

  /** @param {HTMLElement} sticky */
  #restoreStickyBar(sticky) {
    const original = this.#originalStickyState;
    if (!original) return;

    const image = sticky.querySelector('[ref="productImage"]');
    if (image instanceof HTMLImageElement && original.imageSrc) image.src = original.imageSrc;

    sticky.dataset.variantAvailable = original.available;
    this.#originalStickyState = null;
  }

  /** @param {string} imageUrl */
  #swapStickyImage(imageUrl) {
    const sticky = this.#getStickyBar();
    const image = sticky?.querySelector('[ref="productImage"]');
    if (!sticky || !(image instanceof HTMLImageElement)) return;

    this.#saveOriginalStickyBar(sticky);
    image.src = imageUrl;
  }

  /** The sticky button clicks the main add-to-cart button, so it may only be enabled when that one is. */
  #syncStickyButton() {
    const section = this.closest('.shopify-section');
    const mainButton = section?.querySelector('product-form-component add-to-cart-component [ref="addToCartButton"]');
    const stickyButton = this.#getStickyBar()?.querySelector('[ref="addToCartButton"]');
    if (mainButton instanceof HTMLButtonElement && stickyButton instanceof HTMLButtonElement) {
      stickyButton.disabled = mainButton.disabled;
    }
  }

  // ─── Promo Mode: Free Pack Color Picker ────────────────────────────

  /**
   * Prevents clicks inside .bundle-card__free-pack from activating the
   * parent <label>'s radio input. Without this, clicking a swatch triggers
   * handleBundleChange which resets the free pack selection.
   */
  #interceptFreePackClicks() {
    this.querySelectorAll('.bundle-card__free-pack').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, { signal: this.#abortController.signal });
    });
  }

  #buildFreePackSwatches() {
    if (!this.#isPromoMode || !this.#bundleData) return;

    const swatchContainers = this.querySelectorAll('[data-promo-swatches]');

    swatchContainers.forEach((container) => {
      container.innerHTML = '';

      const card = container.closest('.bundle-card');
      if (!card) return;

      const freeQty = parseInt(card.getAttribute('data-free-quantity') ?? '0', 10);
      if (!freeQty) return;

      const regularPack = this.#regularPacks.find((p) => p.packQuantity === freeQty);
      if (!regularPack) return;

      const colors = this.#getUniqueColors(regularPack);

      colors.forEach((color) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'free-pack-swatch';
        button.dataset.color = color.name;
        button.setAttribute('aria-label', color.name);

        if (!color.available) button.disabled = true;
        if (this.#freePackColor === color.name) button.classList.add('free-pack-swatch--selected');

        const swatch = document.createElement('span');
        swatch.className = 'swatch swatch--unscaled';

        // Copy swatch image from the main color picker (same colors, already rendered by Liquid)
        const mainSwatchBg = this.#getMainSwatchBackground(color.name);
        if (mainSwatchBg) {
          swatch.style.setProperty('--swatch-background', mainSwatchBg);
        }

        button.appendChild(swatch);

        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.#selectBundleFromSwatchClick(card);
          this.#handleFreePackColorChange(color.name);
        }, { signal: this.#abortController.signal });

        container.appendChild(button);
      });
    });
  }

  /**
   * Extracts unique color options from a regular pack's variants.
   * Color = first option. Available = any variant with that color + current size is in stock.
   * @param {RegularPack} pack
   * @returns {{name: string, available: boolean}[]}
   */
  #getUniqueColors(pack) {
    const colorMap = new Map();

    for (const variant of pack.variants) {
      if (!variant.options || variant.options.length === 0) continue;
      const color = variant.options[0];

      if (!colorMap.has(color)) {
        colorMap.set(color, { name: color, available: false });
      }

      const currentSize = this.#getCurrentSize();
      if (currentSize) {
        const sizeMatch = variant.options.some((opt, idx) => idx > 0 && opt.toLowerCase() === currentSize.toLowerCase());
        if (sizeMatch && variant.available) {
          colorMap.get(color).available = true;
        }
      } else if (variant.available) {
        colorMap.get(color).available = true;
      }
    }

    return Array.from(colorMap.values());
  }

  /** @returns {string | null} */
  #getCurrentSize() {
    return this.#currentOptions.length > 1 ? this.#currentOptions[1] : null;
  }

  /** @returns {string | null} */
  #getCurrentColor() {
    return this.#currentOptions.length > 0 ? this.#currentOptions[0] : null;
  }

  /**
   * Pre-selects the free pack color to match the main variant picker's active color.
   */
  #preSelectFreePackColor() {
    const currentColor = this.#getCurrentColor();
    if (!currentColor) return;

    const swatches = this.querySelectorAll('.free-pack-swatch:not(:disabled)');
    for (const swatch of swatches) {
      if (swatch.dataset.color?.toLowerCase() === currentColor.toLowerCase()) {
        this.#handleFreePackColorChange(swatch.dataset.color);
        return;
      }
    }
  }

  /**
   * Switches the selected bundle when a swatch in a non-selected card is clicked.
   * @param {Element} card
   */
  #selectBundleFromSwatchClick(card) {
    const cardProductId = parseInt(card.getAttribute('data-product-id') ?? '0', 10);
    if (cardProductId === this.#selectedBundleId) return;

    this.#selectedBundleId = cardProductId;
    this.#updateSelectedState(cardProductId);

    const radio = card.querySelector('input[type="radio"]');
    if (radio instanceof HTMLInputElement) radio.checked = true;

    if (cardProductId !== this.#bundleData?.currentProductId) {
      this.#applyBundleDisplay();
    } else {
      this.#restoreOriginalDisplay();
    }
    this.#updateStickyBar();
    this.#syncColorOptions();
  }

  /** @param {string} color */
  #handleFreePackColorChange(color) {
    this.#freePackColor = color;
    this.#resolveFreePackVariant();
    this.#updateFreePackSelectedState(color);
    this.#updateCardImage(color);
    this.#updateAddToCartState();
  }

  #resolveFreePackVariant() {
    if (!this.#freePackColor) {
      this.#freePackVariantId = null;
      return;
    }

    const bundle = this.#getSelectedPromoBundle();
    if (!bundle) {
      this.#freePackVariantId = null;
      return;
    }

    const regularPack = this.#regularPacks.find((p) => p.packQuantity === bundle.freeQuantity);
    if (!regularPack) {
      this.#freePackVariantId = null;
      return;
    }

    const variant = this.#findRegularPackVariant(regularPack, this.#freePackColor, this.#getCurrentSize());
    this.#freePackVariantId = variant?.id ?? null;
  }

  /**
   * @param {RegularPack} pack
   * @param {string} color
   * @param {string | null} size
   */
  #findRegularPackVariant(pack, color, size) {
    for (const variant of pack.variants) {
      if (!variant.options || variant.options.length === 0) continue;
      if (variant.options[0].toLowerCase() !== color.toLowerCase()) continue;

      if (size && variant.options.length > 1) {
        if (variant.options[1].toLowerCase() === size.toLowerCase()) return variant;
      } else {
        return variant;
      }
    }
    return null;
  }

  /** @param {string} selectedColor */
  #updateFreePackSelectedState(selectedColor) {
    const selectedCard = this.querySelector('.bundle-card--selected');
    if (!selectedCard) return;

    selectedCard.querySelectorAll('.free-pack-swatch').forEach((swatch) => {
      const color = /** @type {HTMLElement} */ (swatch).dataset.color;
      swatch.classList.toggle('free-pack-swatch--selected', color === selectedColor);
    });

    const hint = selectedCard.querySelector('[data-free-pack-hint]');
    if (hint instanceof HTMLElement) {
      hint.style.display = selectedColor ? 'none' : '';
    }
  }

  /** @returns {PromoBundleProduct | null} */
  #getSelectedPromoBundle() {
    if (!this.#bundleData || !this.#selectedBundleId) return null;
    return /** @type {PromoBundleProduct | null} */ (
      this.#bundleData.bundles.find((b) => b.id === this.#selectedBundleId) ?? null
    );
  }

  /**
   * Updates the selected bundle card's image to match the given color.
   * Reads the swatch image from the main color picker DOM.
   * @param {string} color - Color option name (e.g. "Black (original boxer)")
   */
  #updateCardImage(color) {
    const selectedCard = this.querySelector('.bundle-card--selected');
    if (!selectedCard) return;

    const img = selectedCard.querySelector('.bundle-card__image img');
    if (!(img instanceof HTMLImageElement)) return;

    // Read swatch image from the main color picker and resize for the card
    const swatchBg = this.#getMainSwatchBackground(color);
    if (!swatchBg) return;

    const match = swatchBg.match(/url\(["']?([^"')]+)["']?\)/);
    if (!match?.[1]) return;

    // Increase width for the card (200px for retina on 80px display)
    const imageUrl = imageAtWidth(match[1], CARD_IMAGE_WIDTH);
    img.src = imageUrl;
  }

  /**
   * Updates all bundle card images to match the currently selected color.
   * Fetches product JSON to find the variant image for the active color.
   */
  async #updateAllCardImages() {
    const currentColor = this.#getCurrentColor();
    if (!currentColor || !this.#bundleData?.bundles) return;

    const colorLower = currentColor.toLowerCase();

    for (const bundle of this.#bundleData.bundles) {
      if (!bundle.handle) continue;

      const card = this.querySelector(`.bundle-card__input[value="${bundle.id}"]`)?.closest('.bundle-card');
      const img = card?.querySelector('.bundle-card__image img');
      if (!(img instanceof HTMLImageElement)) continue;

      const product = await fetchProductJson(bundle.handle);
      if (!product) continue;

      // Find the first variant matching the selected color
      const matchingVariant = product.variants?.find(
        (v) => v.option1?.toLowerCase() === colorLower
      );
      if (!matchingVariant?.image_id) continue;

      // Find the image by ID
      const variantImage = product.images?.find((i) => i.id === matchingVariant.image_id);
      if (!variantImage?.src) continue;

      // Use 200px width for retina on 80px display
      img.src = imageAtWidth(variantImage.src, CARD_IMAGE_WIDTH);
    }
  }

  /**
   * Updates every bundle card's displayed price (current, compare-at, unit, and
   * savings badge) to match the currently selected color/size. Prices are
   * pre-formatted per variant in Liquid via the `money` filter, so they are
   * market-correct (currency + locale) and need no formatting here.
   */
  #updateAllCardPrices() {
    if (!this.#bundleData?.bundles) return;

    for (const bundle of this.#bundleData.bundles) {
      const card = this.querySelector(`.bundle-card__input[value="${bundle.id}"]`)?.closest('.bundle-card');
      if (!card) continue;

      const variant = this.#findMatchingVariant(bundle, this.#currentOptions);
      if (!variant) continue;

      const currentEl = card.querySelector('.bundle-card__price-current');
      if (currentEl && variant.priceFormatted) currentEl.textContent = variant.priceFormatted;

      const unitEl = card.querySelector('.bundle-card__unit-price');
      if (unitEl && variant.unitPriceFormatted) unitEl.textContent = variant.unitPriceFormatted;

      // Compare-at price: add, update, or remove the span depending on this variant
      const priceWrap = card.querySelector('.bundle-card__price');
      let compareEl = card.querySelector('.bundle-card__price-compare');
      if (variant.compareAtFormatted) {
        if (!compareEl && priceWrap) {
          compareEl = document.createElement('span');
          compareEl.className = 'bundle-card__price-compare';
          priceWrap.appendChild(compareEl);
        }
        if (compareEl) compareEl.textContent = variant.compareAtFormatted;
      } else if (compareEl) {
        compareEl.remove();
      }

      // Savings badge (regular packs only — promo cards have no badges container)
      const badgesWrap = card.querySelector('.bundle-card__badges');
      let savingsEl = card.querySelector('.bundle-badge--savings');

      // BOX-318: a card running a gift campaign already carries that label in this slot,
      // and the card promises one thing at a time.
      const hasGiftLabel = !!badgesWrap?.querySelector('.bundle-badge--gift');

      if (variant.savingsBadgeFormatted && !hasGiftLabel) {
        if (!savingsEl && badgesWrap) {
          savingsEl = document.createElement('span');
          savingsEl.className = 'bundle-badge bundle-badge--savings';
          badgesWrap.appendChild(savingsEl);
        }
        if (savingsEl) savingsEl.textContent = variant.savingsBadgeFormatted;
      } else if (savingsEl) {
        savingsEl.remove();
      }
    }
  }

  // ─── Promo Mode: Add-to-Cart ──────────────────────────────────────

  #interceptAddToCart() {
    if (this.#addToCartIntercepted) return;

    const section = this.closest('.shopify-section');
    const button = section?.querySelector('product-form-component [ref="addToCartButton"]');
    if (!button) return;

    this.#addToCartIntercepted = true;
    button.addEventListener('click', (e) => {
      if (!this.#isPromoMode || this.#isSubmitting) return;
      e.preventDefault();
      this.#submitPromoBundle();
    }, { signal: this.#abortController.signal });
  }

  async #submitPromoBundle() {
    if (this.#isSubmitting) return;

    const bundle = this.#getSelectedPromoBundle();
    if (!bundle || !this.#freePackVariantId) return;

    let paidVariantId = null;
    const section = this.closest('.shopify-section');
    const paidPack = this.#regularPacks.find((p) => p.packQuantity === bundle.paidQuantity);
    if (paidPack) {
      const paidVariant = this.#findRegularPackVariant(paidPack, this.#getCurrentColor(), this.#getCurrentSize());
      if (paidVariant) paidVariantId = paidVariant.id;
    }

    if (!paidVariantId) return;

    this.#isSubmitting = true;
    const addToCartButton = section?.querySelector('[ref="addToCartButton"]');
    const textSpan = addToCartButton?.querySelector('.add-to-cart-text__content span');
    const originalText = textSpan?.textContent ?? '';

    if (addToCartButton instanceof HTMLButtonElement) addToCartButton.disabled = true;
    if (textSpan) textSpan.textContent = '...';

    const bundleId = Date.now().toString();
    const items = [
      {
        id: paidVariantId,
        quantity: 1,
        properties: { '_promo_bundle_id': bundleId, '_promo_type': 'paid' },
      },
      {
        id: this.#freePackVariantId,
        quantity: 1,
        properties: { '_promo_bundle_id': bundleId, '_promo_type': 'free' },
      },
    ];

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      // Apply discount code for this bundle size (configured per size in block settings)
      const discountCodes = JSON.parse(this.dataset.promoDiscountCodes || '{}');
      const discountCode = discountCodes[String(bundle.paidQuantity)];
      if (discountCode) {
        await fetch(`/discount/${encodeURIComponent(discountCode)}`);
      }

      const cartDrawer = document.querySelector('cart-drawer-component');
      const sectionId = cartDrawer?.querySelector('cart-items-component')?.dataset?.sectionId || 'cart-drawer';

      const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionId}`);
      const sectionsData = await sectionsResponse.json();

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { source: 'bundle-picker-promo', data: { sections: sectionsData } },
      }));

      if (cartDrawer && typeof /** @type {any} */ (cartDrawer).open === 'function') {
        /** @type {any} */ (cartDrawer).open();
      }

      if (textSpan) textSpan.textContent = 'Toegevoegd!';
      setTimeout(() => {
        if (textSpan) textSpan.textContent = originalText;
        if (addToCartButton instanceof HTMLButtonElement) addToCartButton.disabled = false;
        this.#isSubmitting = false;
      }, 2000);

    } catch (error) {
      console.error('BundlePicker: Promo add to cart failed:', error);
      if (textSpan) textSpan.textContent = 'Fout - probeer opnieuw';
      setTimeout(() => {
        if (textSpan) textSpan.textContent = originalText;
        if (addToCartButton instanceof HTMLButtonElement) addToCartButton.disabled = false;
        this.#isSubmitting = false;
      }, 2000);
    }
  }

  // ─── Add-to-Cart Button State ──────────────────────────────────────

  /** @param {boolean} [available] */
  #updateAddToCartState(available) {
    const section = this.closest('.shopify-section');
    const productForm = section?.querySelector('product-form-component');
    const addToCartButton = productForm?.querySelector('add-to-cart-component [ref="addToCartButton"]');

    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    if (this.#isPromoMode) {
      const hasFreePack = !!this.#freePackColor && !!this.#freePackVariantId;
      addToCartButton.disabled = !hasFreePack || this.#isSubmitting;
    } else if (available !== undefined) {
      addToCartButton.disabled = !available;
    }

    this.#syncStickyButton();
  }
}

if (!customElements.get('bundle-picker-component')) {
  customElements.define('bundle-picker-component', BundlePicker);
}
